import { useEffect, useRef } from "react";
import { ArrowUp } from "lucide-react";

/**
 * El cuadro para escribirle a Atlas, con la MISMA cara que la barra del chat:
 * tarjeta blanca flotando con sombra y anillo, textarea sin borde que crece
 * solo, botón redondo de enviar abajo a la derecha, Enter manda y Shift+Enter
 * hace salto de línea.
 *
 * ES UNA COPIA DEL LOOK, NO UN REUSO DE `BarraEntradaChat`, y es a propósito.
 * Aquella barra trae adjuntos, el menú "+" con los interruptores del chat
 * (Investigación, Respuestas gráficas) y el botón de detener el streaming.
 * Nada de eso existe en una puesta en marcha: reusarla obligaría a inventarle
 * un estado de chat a un formulario y a apagar la mitad de sus controles con
 * props, y el primer interruptor que se sume al chat aparecería acá adentro sin
 * que nadie lo haya decidido. Lo que se comparte —los radios concéntricos
 * (16 = 12 + 4), la sombra, el anillo que se prende al enfocar, el botón de
 * 36px— es la parte que tiene que verse igual, y se ve igual.
 *
 * Ver ADR-2026-08-18-estudio-marketing-paso-estrategia-meta-ads.
 */

export interface PropsCuadroPrompt {
  valor: string;
  onCambiar: (texto: string) => void;
  onEnviar: () => void;
  /** `false` apaga el botón de enviar (cuadro vacío). */
  puedeEnviar: boolean;
  placeholder: string;
  /** Rótulo accesible del textarea: no hay `<label>` visible, el título de la
   *  pantalla hace ese trabajo a la vista pero no para un lector. */
  etiqueta: string;
  autoFoco?: boolean;
}

/** Hasta dónde crece el cuadro antes de scrollear adentro. Más alto que el del
 *  chat (160): acá no hay una conversación abajo a la que comerle lugar, y lo
 *  que se pide es un relato de varios renglones. */
const ALTO_MAXIMO = 260;

export const CuadroPrompt = ({
  valor,
  onCambiar,
  onEnviar,
  puedeEnviar,
  placeholder,
  etiqueta,
  autoFoco,
}: PropsCuadroPrompt) => {
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize con tope. El `height = "auto"` antes de medir no es adorno: sin
  // eso `scrollHeight` nunca baja y el cuadro no se achica al borrar texto.
  useEffect(() => {
    const elemento = areaRef.current;
    if (!elemento) return;
    elemento.style.height = "auto";
    elemento.style.height = `${Math.min(elemento.scrollHeight, ALTO_MAXIMO)}px`;
  }, [valor]);

  useEffect(() => {
    if (autoFoco) areaRef.current?.focus();
  }, [autoFoco]);

  return (
    /* Radios concéntricos: tarjeta 16 = textarea/controles 12 + 4. */
    <div className="rounded-2xl bg-white p-1 shadow-[0_2px_14px_-6px_rgb(var(--brand-black-rgb)_/_0.18),0_1px_3px_rgb(var(--brand-black-rgb)_/_0.06)] ring-1 ring-[rgb(var(--brand-black-rgb)_/_0.06)] transition-shadow duration-200 focus-within:ring-[rgb(var(--brand-primary-rgb)_/_0.28)]">
      <textarea
        ref={areaRef}
        value={valor}
        aria-label={etiqueta}
        onChange={(evento) => onCambiar(evento.target.value)}
        onKeyDown={(evento) => {
          if (evento.key === "Enter" && !evento.shiftKey) {
            evento.preventDefault();
            if (puedeEnviar) onEnviar();
          }
        }}
        placeholder={placeholder}
        rows={3}
        className="w-full resize-none bg-transparent px-3 pt-4 text-[15px] leading-snug text-[rgb(var(--brand-black-rgb)_/_0.9)] outline-none placeholder:text-[rgb(var(--brand-black-rgb)_/_0.38)]"
      />

      <div className="flex items-center justify-end px-2 pb-2 pt-2">
        <button
          type="button"
          onClick={onEnviar}
          disabled={!puedeEnviar}
          aria-label="Enviar"
          className={`inline-flex h-9 w-9 items-center justify-center rounded-full shadow-sm transition-colors duration-200 ${
            puedeEnviar
              ? "bg-brand-primary text-white hover:bg-brand-primary-hover"
              : "cursor-not-allowed bg-[#F0F2F7] text-[rgb(var(--brand-black-rgb)_/_0.3)]"
          }`}
        >
          <ArrowUp className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default CuadroPrompt;
